import OutsideButton from '@gaia/components/OutsideButton';
import {bannerKey, useBanner} from '@gaia/hooks/useBanner';
import usePersistentState from '@gaia/hooks/usePersistentState';
import {Close} from '@mui/icons-material';
import {Collapse, IconButton, Typography} from '@mui/material';

import classes from './OutsideAnnouncement.module.css';

interface OutsideAnnouncementProps {
    isMigratedRivtUser: boolean;
    hasActiveOutsidePlusSubscription: boolean;
}

interface OutsideAnnouncementControlledProps {
    localStorageKey: string;
    dismissable: boolean;
    message: string;
    // The text of the button
    cta?: string;
    // The button link
    href?: string;
    className?: string;
}

export function OutsideAnnouncement({
    isMigratedRivtUser,
    hasActiveOutsidePlusSubscription,
}: OutsideAnnouncementProps) {
    const banner = useBanner();

    if (!banner) return null;

    if (banner.message) {
        return (
            <OutsideAnnouncementControlled
                dismissable={false}
                localStorageKey={`${bannerKey}__${banner.hash}`}
                message={banner.message}
            />
        );
    }

    if (!isMigratedRivtUser) {
        return (
            <OutsideAnnouncementControlled
                dismissable={false}
                localStorageKey=''
                message='Merge your Gaia GPS and Outside account today to get all the benefits of Outside+'
                href='/settings/#merge-accounts'
                cta='View your account'
            />
        );
    }

    if (!hasActiveOutsidePlusSubscription) {
        return (
            <OutsideAnnouncementControlled
                dismissable={true}
                localStorageKey='showMigratedAnnouncement'
                message='Unlock Gaia GPS Premium, Trailforks Pro, &amp; Outside Watch'
                href='/outsideplus/?utm_medium=banner&utm_source=website&utm_campaign=o-plus'
                cta='Join Outside+'
            />
        );
    }

    return null;
}

export function OutsideAnnouncementControlled({
    localStorageKey,
    dismissable,
    message,
    cta,
    href,
    className,
}: OutsideAnnouncementControlledProps) {
    const [shouldShowBanner, setShowBanner] = usePersistentState(
        `outsideAnnouncement__${localStorageKey}`,
        true
    );

    return (
        <Collapse in={shouldShowBanner} className={className}>
            <div className={classes.container}>
                <div className={classes.content}>
                    <Typography variant='body2' align='center'>
                        {message}
                    </Typography>

                    {cta && (
                        <OutsideButton
                            appearance='secondary'
                            size='small'
                            href={href}
                            target='_blank'
                            rel='noopener noreferrer'
                        >
                            {cta}
                        </OutsideButton>
                    )}
                </div>

                {dismissable && (
                    <IconButton
                        aria-label='Close'
                        onClick={() => setShowBanner(false)}
                        className={classes.button}
                        size='large'
                    >
                        <Close />
                    </IconButton>
                )}
            </div>
        </Collapse>
    );
}
